import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'

import useProbateEstateCollection from 'lib/probate/probate-estate-collection'
import {
  ProbateEstatePerson,
  ProbateEstatePeopleAttributes,
  ProbateEstateProperty,
} from 'lib/models/probate-estate'

// a role is taken away by clearing its attributes, so any of them can be null
export type PersonAttributes = {
  [Key in keyof ProbateEstatePeopleAttributes]?:
    ProbateEstatePeopleAttributes[Key] | null
}

export type EstatePeople = {
  people: ProbateEstatePerson[]
  createPerson: (attributes: PersonAttributes) => Promise<ProbateEstatePerson>
  updatePerson: (id: number, attributes: PersonAttributes) => Promise<unknown>
  deletePerson: (id: number) => Promise<unknown>
}

type ChangedPeople = Record<number, ProbateEstatePerson>

const nothingToDo = () => Promise.reject(new Error('No estate people'))

export const EstatePeopleContext = createContext<EstatePeople>({
  people: [],
  createPerson: nothingToDo,
  updatePerson: nothingToDo,
  deletePerson: nothingToDo,
})

type PropertyCollection = ReturnType<typeof useProbateEstateCollection>

export const EstatePropertiesContext = createContext<PropertyCollection | null>(
  null
)

export const EstatePeopleProvider = ({
  probateEstateId,
  children,
}: {
  probateEstateId: number
  children: ReactNode
}) => {
  const { items, createItem, updateItem, deleteItem } =
    useProbateEstateCollection({
      probateEstateId,
      collection: 'people',
      type: 'probate_estate_people',
    })
  const saved = items as ProbateEstatePerson[]
  const savedRef = useRef(saved)
  savedRef.current = saved

  // a change to someone is shown straight away, then what was saved, until
  // the list catches up
  const [changed, setChanged] = useState<ChangedPeople>({})
  // only the latest change to someone decides what's shown for them
  const versions = useRef(new Map<number, number>())

  const people = useMemo(
    () => saved.map((person) => changed[person.id] ?? person),
    [saved, changed]
  )

  useEffect(() => {
    setChanged((current) => {
      const caughtUp = Object.keys(current).filter((id) =>
        isEqual(
          saved.find((person) => person.id === Number(id)),
          current[Number(id)]
        )
      )
      return caughtUp.length
        ? (omit(current, caughtUp) as ChangedPeople)
        : current
    })
  }, [saved])

  const updatePerson = useCallback(
    async (id: number, attributes: PersonAttributes) => {
      const version = (versions.current.get(id) ?? 0) + 1
      versions.current.set(id, version)
      const isLatest = () => versions.current.get(id) === version

      setChanged((current) => {
        const person =
          current[id] ?? savedRef.current.find((other) => other.id === id)
        if (!person) return current
        return {
          ...current,
          [id]: {
            ...person,
            attributes: { ...person.attributes, ...attributes },
          } as ProbateEstatePerson,
        }
      })

      try {
        const savedPerson = await updateItem(id, attributes)
        if (isLatest() && savedPerson)
          setChanged((current) => ({ ...current, [id]: savedPerson }))
        return savedPerson
      } catch (error) {
        if (isLatest())
          setChanged((current) => omit(current, id) as ChangedPeople)
        throw error
      }
    },
    [updateItem]
  )

  const estatePeople = useMemo(
    () => ({
      people,
      createPerson: createItem,
      updatePerson,
      deletePerson: deleteItem,
    }),
    [people, createItem, updatePerson, deleteItem]
  )

  const properties = useProbateEstateCollection({
    probateEstateId,
    collection: 'properties',
    type: 'probate_estate_properties',
  })

  return (
    <EstatePeopleContext.Provider value={estatePeople}>
      <EstatePropertiesContext.Provider value={properties}>
        {children}
      </EstatePropertiesContext.Provider>
    </EstatePeopleContext.Provider>
  )
}

export const useEstatePeople = () => useContext(EstatePeopleContext)

export const useEstateProperties = () => {
  const properties = useContext(EstatePropertiesContext)
  if (!properties) throw new Error('No estate properties')
  return properties
}

/** Each property someone co-owns counts as a role. */
export const useRoleCount = () => {
  const properties = (useContext(EstatePropertiesContext)?.items ??
    []) as ProbateEstateProperty[]

  return ({ id, attributes }: ProbateEstatePerson) =>
    [
      attributes.personalRepresentativeType,
      attributes.isBeneficiary,
      attributes.isDependent,
    ].filter(Boolean).length +
    properties.filter((property) =>
      property.attributes.coOwnerIds?.includes(id)
    ).length
}
